import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import SectionHeading from '../components/SectionHeading';
import OrderBand from '../components/OrderBand';
import { ArrowRight, Clock } from '../components/Icons';
import { EVENTS, WEEK } from '../data/events';
import { LOCATIONS } from '../data/site';
import { PHOTO } from '../data/media';
import './WhatsOn.css';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function WhatsOn() {
  const todayIndex = new Date().getDay();
  const today = DAY_NAMES[todayIndex];
  const onToday = EVENTS.filter((e) => e.day.includes(todayIndex));

  return (
    <>
      <Seo
        title="What's On"
        description="Burger Mondays, Taco Tuesday, Happy Hour and Slow Sundays. The weekly line-up at The Local, Bugolobi and Kyanja."
        path="/whats-on"
      />

      <PageHero
        eyebrow="The weekly line-up"
        title="Every week has a reason."
        lead="Four fixtures the neighbourhood plans around — plus a bar that stays open a little longer than it should."
        image={PHOTO.signatures}
      >
        <Link to="/bookings" className="btn btn--light">
          <span>Book a table</span>
          <ArrowRight />
        </Link>
      </PageHero>

      {/* Today ------------------------------------------------------------ */}
      <section className="section--tight today">
        <div className="page today__inner">
          <p className="eyebrow">On today · {today}</p>
          <p className="today__list">
            {onToday.length ? (
              onToday.map((e, i) => (
                <span key={e.id}>
                  <strong>{e.name}</strong> <span className="tabular">{e.time}</span>
                  {i < onToday.length - 1 ? <span className="today__sep">·</span> : null}
                </span>
              ))
            ) : (
              <span>Kitchen and bar open as usual. No fixtures today — just the menu.</span>
            )}
          </p>
        </div>
      </section>

      {/* Events ----------------------------------------------------------- */}
      <section className="section events">
        <div className="page stack stack-lg">
          {EVENTS.map((ev, i) => {
            const flip = i % 2 === 1;
            const on = ev.day.includes(todayIndex);
            return (
              <article className={`event${flip ? ' event--flip' : ''}`} key={ev.id} id={ev.id}>
                <Reveal className="event__media" variant="zoom" delay={0.05}>
                  <div className="frame frame--3x2 frame--hover">
                    <img src={ev.image} alt={ev.name} loading="lazy" />
                  </div>
                  {on ? <span className="event__today">On today</span> : null}
                </Reveal>

                <div className="event__body">
                  <Reveal as="p" className="event__index tabular" delay={0.05}>
                    0{i + 1}
                  </Reveal>
                  <Reveal as="h2" className="display d3" delay={0.1}>
                    {ev.name}
                  </Reveal>
                  <Reveal as="p" className="event__when" delay={0.15}>
                    <Clock size={14} />
                    <span>
                      {ev.when} <span className="tabular">· {ev.time}</span>
                    </span>
                  </Reveal>
                  <Reveal as="p" className="event__blurb italic" delay={0.2}>
                    {ev.blurb}
                  </Reveal>
                  <Reveal as="p" className="body" delay={0.25}>
                    {ev.body}
                  </Reveal>
                  <Reveal className="event__actions" delay={0.3}>
                    <Link to={`/whats-on/${ev.id}`} className="link">
                      More about {ev.name} <ArrowRight />
                    </Link>
                  </Reveal>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Week grid -------------------------------------------------------- */}
      <section className="section section--deep">
        <div className="page stack stack-lg">
          <SectionHeading
            eyebrow="At a glance"
            title="The whole week, on one page."
            align="center"
          />

          <ul className="week">
            {WEEK.map((row, i) => (
              <Reveal
                as="li"
                key={row.day}
                className={`week__row${row.day === today ? ' is-today' : ''}`}
                delay={i * 0.04}
              >
                <span className="week__day">{row.day}</span>
                <span className="week__items">
                  {row.items.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </span>
              </Reveal>
            ))}
          </ul>

          <Reveal as="p" className="week__note" delay={0.2}>
            Both branches run the same line-up. Kitchen times can shift on public
            holidays — call{' '}
            {LOCATIONS.map((l, i) => (
              <React.Fragment key={l.id}>
                <a href={`tel:${l.phoneIntl}`} className="tabular">
                  {l.name} {l.phone}
                </a>
                {i < LOCATIONS.length - 1 ? ' or ' : ''}
              </React.Fragment>
            ))}{' '}
            if you're planning around one.
          </Reveal>
        </div>
      </section>

      <OrderBand compact />
    </>
  );
}
